/**
 * The workspace data setting, "Send transcript and diff content" (WP-15): GET and PUT
 * /api/policy/data, live through the policy module's 'data' event; demo data in ?mock=1.
 */
import { useCallback, useEffect, useState } from 'react';
import { DATA_SHARING_DEFAULT } from '@orgus/protocol';
import type { DataSharing } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

let mockSetting: DataSharing = { ...DATA_SHARING_DEFAULT };
const subscribers = new Set<(d: DataSharing) => void>();
const publish = (d: DataSharing) => {
  for (const fn of subscribers) fn(d);
};

export type DataSharingState = { kind: 'loading' } | { kind: 'error'; error: unknown } | { kind: 'ready'; data: DataSharing };

export function useDataSharing(): { state: DataSharingState; retry: () => void; save: (content: boolean) => Promise<void> } {
  const mock = useOrgus((s) => s.mode === 'mock');
  const [state, setState] = useState<DataSharingState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    const set = (data: DataSharing) => !ctrl.signal.aborted && setState({ kind: 'ready', data });
    subscribers.add(set);
    if (mock) set(mockSetting);
    else {
      api<DataSharing>('/api/policy/data', { signal: ctrl.signal })
        .then(set)
        .catch((error: unknown) => !ctrl.signal.aborted && setState({ kind: 'error', error }));
    }
    return () => {
      ctrl.abort();
      subscribers.delete(set);
    };
  }, [mock, tick]);

  const save = useCallback(
    async (content: boolean) => {
      const next = mock ? { content, updatedAt: Date.now(), updatedBy: 'you' } : await api<DataSharing>('/api/policy/data', { method: 'PUT', body: { content } });
      if (mock) mockSetting = next;
      publish(next);
    },
    [mock],
  );

  return { state, retry: () => setTick((n) => n + 1), save };
}
